import { ageIgnore, itemUseIgnore, traitIgnore, narrativeOrder, rankBetween, changesOf, displayOf, entityName as titleOf, formatSceneTime, isCondition, isPlaceEntity, isScene, mentionsIn, parseTimeInput, leakIgnore, learningsIn, lifeIgnore, placeOf, povSuggestionIgnore, priorText, previewMark, refIndex, sameChange, settingConflictIgnore, shiftIgnored, stateIgnored, stateRoleOf, termIndex, termsOf, timeConflictIgnore, type Entity, type MarkRole, type SceneTime, type StateChange, type StateHint, type IgnoredTerm, type MarkFix, type SpanRole, type StoryNode, type TermHit, type TermIndex, type TimePrecision, type TimeSuggestion } from '@dispositio/core';
import { Extension, getMarkRange, type Range } from '@tiptap/core';
import Placeholder from '@tiptap/extension-placeholder';
import type { MarkType, Node as PMNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { EditorContent, useEditor, useEditorState, type Editor } from '@tiptap/react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { de } from '../../i18n/de';
import { linkPresetOf, shortcutList, type LinkPreset } from '../../lib/linkShortcuts';
import { placeShift, placeShiftKey, type ShiftAct, type ShiftHit, type ShiftLive } from '../../lib/placeShift';
import { lifeConflictKey, lifeConflictMarks, type LifeHit, type LifeLive } from '../../lib/lifeConflicts';
import { factConflictKey, factConflictMarks, type FactHit, type FactLive } from '../../lib/factConflicts';
import { knowledgeLeakKey, knowledgeLeakMarks, type LeakHit, type LeakLive } from '../../lib/knowledgeLeaks';
import { sceneSplitKey, sceneSplitMarks, splitIgnore, type SplitHit, type SplitLive } from '../../lib/sceneSplit';
import { povSuggestKey, povSuggestMarks, settingConflictKey, settingConflictMarks, type PovHit, type PovLive, type SettingAct, type SettingHit, type SettingLive } from '../../lib/settingConflicts';
import { mentionTokenAt } from '../../lib/refPicker';
import { docToField, fieldToDoc, WORD } from '../../lib/refLink';
import { spanOver, type SpanRange } from '../../lib/refSpan';
import { stateHintKey, stateHints, type StateAct, type StateLive } from '../../lib/stateHints';
import { jumpToSuggestion, openSuggestions, suggestionCounts, suggestKeys, type SuggestAct, type SuggestKind } from '../../lib/suggest';
import { changeText } from '../../lib/stateText';
import { cleanMarkdown, fieldTextExtensions, richTextExtensions } from '../../lib/richText';
import { termKey, termMarks, type TermAct, type TermLive } from '../../lib/termMarks';
import { timeOver, timeRanges } from '../../lib/timeMark';
import { markAt, timeConflictKey, timeConflicts, type ConflictAct, type ConflictHit, type ConflictLive } from '../../lib/timeConflicts';
import { timeGuardKey, timeMarkGuard, type LostTime } from '../../lib/timeMarkGuard';
import { describeTimeMark } from '../../lib/timeMarkText';
import { timeSuggest, timeSuggestKey, type TimeAct, type TimeLive } from '../../lib/timeSuggest';
import { MARK_VIEWS, quietClasses, useMarkView } from '../../state/markView';
import { usePinnedIds, useRefPins } from '../../state/refPins';
import { useStoryEnv } from '../../state/storyEnv';
import { toast } from '../../state/toast';
import { EntityModal } from '../design/EntityModal';
import { EditorIcon, type EditorIconKind } from './EditorIcons';
import { RefBrowser, type RefKind } from './RefBrowser';
import { RefGlance } from './RefGlance';
import { RefPins, type PinChoice } from './RefPins';
import { RefRemover, type RemovableRef } from './RefRemover';
import { SpanGutter, spanFocus } from './SpanGutter';
import { TermPicker } from './TermPicker';
import { TimeMarkEditor, type TimeMarkValue } from './TimeMarkEditor';

interface Props {
  /** Markdown (Dokument) bzw. reiner Text mit `Wort[@schlüssel]` (Feld) */
  value: string;
  onChange: (text: string) => void;
  /** `document`: Textdokument als Seite mit Werkzeugleiste, gespeichert als Markdown; `field`: schlankes Textfeld (Kurzbeschreibung u. a.) */
  variant?: 'document' | 'field';
  testId?: string;
  /** Strg+S: sofort speichern */
  onSave: () => void;
  onBlur: () => void;
  /** Der Node, zu dessen Szene der Text gehört (für die Auswahl bei @; Einträge der Szene stehen oben) */
  node?: StoryNode;
  placeholder: string;
  ariaLabel: string;
  /** Bewusst nicht verknüpfte Stellen dieses Dokuments */
  ignored?: IgnoredTerm[];
  /** Eine Stelle ignorieren (✕ am Textmarker). Ohne: keine möglichen Verweise (z. B. in einfachen Textfeldern) */
  onIgnore?: (entry: IgnoredTerm) => void;
  /** Prüfen: an allen möglichen Verweisen ✓/✕, oben eine Leiste mit „Alle annehmen“ */
  review?: boolean;
  onReview?: (on: boolean) => void;
  /**
   * Die Szene, deren Zeit die Zeitmarken dieses Textes bestimmen (ihr Draft); sonst ändern sie keine Zeit (PLAN.md §16). Ebenso folgt ihr
   * Schauplatz den verknüpften Orten, Ortswechsel erscheinen als Hinweis (§16a).
   */
  timeScene?: StoryNode | null;
}

const NO_TERMS: TermIndex = { byFirst: new Map() };

/** Allein stehender Verweis: `[@schlüssel]` oder ein schlichtes `@schlüssel` (verknüpfte Wörter sind eine eigene Markierung, siehe refLink) */
const REF = /()\[@([a-z][a-z0-9_]{0,29})\]|(^|[^\w@[])@([a-z][a-z0-9_]{0,29})/gu;

const highlightKey = new PluginKey('refHighlight');
const targetKey = new PluginKey<DecorationSet>('linkTarget');

/**
 * Allein stehende Verweise (`[@schlüssel]`, `@schlüssel`) erscheinen als Chip, unbekannte rot gestrichelt. Verknüpfte Wörter
 * („Sarah“ mit Sarah Baier) sind eine eigene Markierung und blau (siehe refLink).
 */
const refHighlight = (known: { current: Set<string> }): Extension =>
  Extension.create({
    name: 'refHighlight',
    addProseMirrorPlugins() {
      const build = (doc: Parameters<typeof DecorationSet.create>[0]): DecorationSet => {
        const decos: Decoration[] = [];
        doc.descendants((node, pos) => {
          if (!node.isText || !node.text || node.marks.some((m) => m.type.name === 'refLink')) return;
          for (const m of node.text.matchAll(REF)) {
            const refKey = m[2] ?? m[4]!;
            const from = pos + m.index! + (m[2] ? 0 : m[3]!.length);
            const length = m[2] ? m[0].length : refKey.length + 1;
            decos.push(Decoration.inline(from, from + length, { class: known.current.has(refKey) ? 'rt-ref known' : 'rt-ref unknown', 'data-ref': refKey }));
          }
        });
        return DecorationSet.create(doc, decos);
      };
      return [
        new Plugin({
          key: highlightKey,
          state: { init: (_, state) => build(state.doc), apply: (tr, old) => (tr.docChanged || tr.getMeta(highlightKey) ? build(tr.doc) : old) },
          props: { decorations: (state) => highlightKey.getState(state) as DecorationSet },
        }),
      ];
    },
  });

/** Die Textstelle, die gerade verknüpft wird, bleibt sichtbar markiert, solange der Referenz-Browser offen ist. */
const linkTarget = Extension.create({
  name: 'linkTarget',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: targetKey,
        state: {
          init: () => DecorationSet.empty,
          apply: (tr, old) => {
            const meta = tr.getMeta(targetKey) as Range | null | undefined;
            if (meta === null) return DecorationSet.empty;
            if (meta) return DecorationSet.create(tr.doc, [Decoration.inline(meta.from, meta.to, { class: 'rt-link-target' })]);
            return old.map(tr.mapping, tr.doc);
          },
        },
        props: { decorations: (state) => targetKey.getState(state) },
      }),
    ];
  },
});

/** Angefangenes `@…` direkt vor dem Cursor im aktuellen Absatz, mit seiner Lage im Dokument. */
function tokenAt(editor: Editor): { query: string; from: number; to: number } | null {
  const { $from, empty } = editor.state.selection;
  if (!empty || !$from.parent.isTextblock) return null;
  const before = $from.parent.textBetween(0, $from.parentOffset, undefined, '￼');
  const t = mentionTokenAt(before, before.length);
  if (!t) return null;
  return { query: t.query, from: $from.pos - t.query.length - 1, to: $from.pos };
}

const WORD_CHAR = new RegExp(WORD, 'u');
const escapeRe = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Was „Verknüpfen“ verknüpft: die Markierung (ohne Leerzeichen am Rand); ohne Markierung die Verknüpfung bzw. das Wort am Cursor. */
function linkRange(editor: Editor): Range | 'empty' | 'blocks' {
  const { state } = editor;
  const { from, to, empty, $from, $to } = state.selection;
  const linkType = state.schema.marks.refLink!;
  let range: Range = { from, to };
  if (empty) {
    const inLink = getMarkRange($from, linkType);
    if (inLink) range = inLink;
    else {
      if (!$from.parent.isTextblock) return 'empty';
      const text = $from.parent.textBetween(0, $from.parent.content.size, undefined, '￼');
      let a = $from.parentOffset;
      let b = $from.parentOffset;
      while (a > 0 && WORD_CHAR.test(text[a - 1]!)) a--;
      while (b < text.length && WORD_CHAR.test(text[b]!)) b++;
      if (a === b) return 'empty';
      range = { from: $from.start() + a, to: $from.start() + b };
    }
  } else if (!$from.sameParent($to)) {
    return 'blocks';
  }
  const text = state.doc.textBetween(range.from, range.to, ' ', '￼');
  const lead = text.length - text.trimStart().length;
  const trail = text.length - text.trimEnd().length;
  range = { from: range.from + lead, to: range.to - trail };
  return range.from < range.to ? range : 'empty';
}

/** Weitere Stellen mit genau diesem Text, die noch nicht verknüpft sind (als ganze Wörter, außerhalb der Markierung). */
function otherPlaces(doc: PMNode, text: string, except: Range, linkType: MarkType): Range[] {
  if (text.length > 60 || text.trim() === '') return [];
  const re = new RegExp(`(?<![\\p{L}\\p{N}_])${escapeRe(text)}(?![\\p{L}\\p{N}_])`, 'gu');
  const out: Range[] = [];
  doc.descendants((node, pos) => {
    if (!node.isTextblock) return true;
    const t = node.textBetween(0, node.content.size, undefined, '￼');
    for (const m of t.matchAll(re)) {
      const from = pos + 1 + m.index!;
      const to = from + text.length;
      if ((to <= except.from || from >= except.to) && !doc.rangeHasMark(from, to, linkType)) out.push({ from, to });
    }
    return false;
  });
  return out;
}

/** Schlüssel einer Verknüpfung in der Markierung (falls es eine gibt) */
function linkedKeyIn(doc: PMNode, range: Range): string | null {
  let key: string | null = null;
  doc.nodesBetween(range.from, range.to, (node) => {
    const m = node.marks.find((x) => x.type.name === 'refLink');
    if (m && key === null) key = String(m.attrs.key ?? '') || null;
  });
  return key;
}

/** Ende eines getippten „@…“ ab der Lage des „@“ (Buchstaben, Ziffern und Unterstrich danach) */
function tokenEnd(doc: PMNode, at: number): number {
  const rest = doc.textBetween(at, Math.min(doc.content.size, at + 40), '\n', '￼');
  return at + (/^@[A-Za-z0-9_]*/.exec(rest)?.[0].length ?? 0);
}

interface BrowserState {
  /** die Stelle, die verknüpft wird (Markierung bzw. das Wort direkt vor dem „@“); null: an die Stelle des „@“ kommt ein gewähltes Wort */
  range: Range | null;
  text: string;
  linkedKey: string | null;
  others: Range[];
  anchor: { left: number; top: number; bottom: number };
  /** mit „@“ geöffnet: wo das „@“ steht – das „@…“ verschwindet beim Verknüpfen */
  at: number | null;
  /** nach dem „@“ schon Getipptes: Anfang der Suche */
  query: string;
  /** Nennung oder Bezug; `kinds`: umschaltbar (nur mit Markierung in einem Dokument) */
  kind: RefKind;
  kinds: boolean;
  /** Art des Bezugs, per Tastenkürzel gewählt */
  role?: SpanRole;
  /** Favoriten: die Elemente, die im Text schon vorkommen (in der Reihenfolge ihres ersten Vorkommens) */
  favorites: string[];
  /** Bezug, der an der Stelle schon besteht */
  span: { keys: string[]; role: SpanRole | null } | null;
}

/** Klingt die Markierung nach einem Namen (bis vier Wörter, keine Satzzeichen darin)? Sonst ist sie eher eine Stelle, die zu jemandem gehört. */
const nameLike = (text: string): boolean => {
  const t = text.trim();
  return (t.match(new RegExp(`${WORD}+`, 'gu'))?.length ?? 0) <= 4 && !/[.!?;:,„“”"«»]/.test(t.replace(/[.!?,;:]+$/, ''));
};

const LINE_TYPES = [
  { value: 'p', label: () => de.editor.paragraphType },
  { value: '1', label: () => de.editor.headingLevel(1) },
  { value: '2', label: () => de.editor.headingLevel(2) },
  { value: '3', label: () => de.editor.headingLevel(3) },
] as const;

/**
 * Texteditor ohne Markdown-Kenntnisse: als Seite wie in einer Textverarbeitung, mit Werkzeugleiste (Rückgängig, Absatzformat, fett,
 * kursiv, durchgestrichen, Listen, Zitat) und „Verknüpfen“: ein Wort oder eine ganze Passage markieren und im Referenz-Browser mit
 * einem Eintrag verknüpfen (oder ihn dort neu anlegen). Ein getipptes „@“ öffnet denselben Browser – für das Wort davor bzw. für einen
 * Verweis an dieser Stelle (auch in einfachen Textfeldern). Gespeichert wird Markdown. Im Browser angeheftete Elemente stehen rechts
 * neben der Seite und verknüpfen die Markierung mit einem Klick (siehe RefPins).
 */
export function RichTextEditor({ value, onChange, onSave, onBlur, node, placeholder, ariaLabel, variant = 'document', testId, ignored, onIgnore, review = false, onReview, timeScene = null }: Props) {
  const isField = variant === 'field';
  const { ctx, goTo, projectId, actions } = useStoryEnv();
  // mögliche Verweise (nicht verknüpfte Namen und Vorkommen): nur in Dokumenten
  const withTerms = !isField && !!onIgnore;
  const terms = useMemo(() => (withTerms ? termIndex(ctx) : NO_TERMS), [ctx, withTerms]);
  const termLive = useRef<TermLive>({ index: terms, ignored, review, act: () => undefined, title: () => '', labels: { accept: de.terms.accept, ignore: de.terms.ignore } });
  // mögliche Zeitangaben (PLAN.md §16): ebenfalls nur in Dokumenten
  const timeLive = useRef<TimeLive>({ ignored, review, act: () => undefined, title: () => '', labels: { accept: de.timeMarks.accept, ignore: de.timeMarks.ignore } });
  // Szene teilen, wo der Text es nahelegt (PF 51): Szenentrenner bzw. Zeitsprung am Absatzanfang mitten im Draft
  const splitLive = useRef<SplitLive>({ scene: timeScene, ignored, review, act: () => undefined, title: () => '', labels: { accept: de.sceneSplit.accept, ignore: de.sceneSplit.ignore } });
  // mögliche Ortswechsel im Draft einer Szene (PLAN.md §16a)
  const shiftLive = useRef<ShiftLive>({ on: false, placeOf: () => null, setting: null, ignored, review, act: () => undefined, title: () => '', labels: { accept: de.places.accept, ignore: de.places.ignore } });
  // mögliche Zustandsänderungen im Draft einer Szene (PLAN.md §16c)
  const stateLive = useRef<StateLive>({ on: false, roleOf: () => null, isSet: () => false, ignored, review, act: () => undefined, title: () => '', labels: { accept: de.states.accept, ignore: de.states.ignore } });
  // Zeitmarken, die nicht mehr stimmen (PLAN.md §16d): rot an der Stelle
  const conflictLive = useRef<ConflictLive>({
    ctx,
    scene: timeScene,
    ignored,
    review,
    act: () => undefined,
    title: () => '',
    labels: { accept: '', ignore: de.timeConflicts.intended },
    hitLabels: () => ({ accept: null, ignore: de.timeConflicts.intended }),
    other: () => null,
  });
  // Text gegen Angabe von Hand (PF 56): Schauplatz bzw. Perspektive anders gesetzt, als der Draft sagt – rot an der Stelle
  const settingLive = useRef<SettingLive>({
    ctx,
    scene: timeScene,
    entityOf: () => null,
    placeOf: () => null,
    ignored,
    review,
    act: () => undefined,
    title: () => '',
    labels: { accept: '', ignore: de.settingConflicts.intended },
    hitLabels: () => ({ accept: null, ignore: de.settingConflicts.intended }),
  });
  // Lebt die Figur hier? (AP-42): Rede, Handlung, Gedanke einer Figur, die zu der Zeit tot bzw. noch nicht geboren ist – rot an der Stelle
  const lifeLive = useRef<LifeLive>({ ctx, scene: timeScene, entityOf: () => null, ignored, review, act: () => undefined, title: () => '', other: () => null, labels: { accept: '', ignore: de.lifeConflicts.intended } });
  // Alter, Merkmal, benutzter Gegenstand (AP-40, AP-42): was der Text über eine Figur sagt und zu der Zeit nicht stimmt – rot an der Stelle
  const factLive = useRef<FactLive>({ ctx, scene: timeScene, entityOf: () => null, ignored, review, act: () => undefined, title: () => '', other: () => null, labels: { accept: '', ignore: de.ageConflicts.intended } });
  // Wissenslecks (AP-43): Rede bzw. Gedanke über etwas, das die Figur erst später erfährt – rot an der Stelle
  const leakLive = useRef<LeakLive>({ ctx, scene: timeScene, entityOf: () => null, ignored, review, act: () => undefined, title: () => '', other: () => null, labels: { accept: '', ignore: de.knowledgeLeaks.intended } });
  // Perspektive aus dem Text (PF 50): keine gesetzt, Gedanken nur einer Person – gelb an ihrem ersten Gedanken
  const povLive = useRef<PovLive>({ ctx, scene: timeScene, entityOf: () => null, ignored, review, act: () => undefined, title: () => '', labels: { accept: de.povSuggest.accept, ignore: de.povSuggest.ignore } });
  const known = useRef(new Set<string>());
  known.current = useMemo(() => new Set(refIndex(ctx).keys()), [ctx]);
  const [browser, setBrowser] = useState<BrowserState | null>(null);
  // nach einem allein stehenden „@“ und der Wahl des Elements: mit welchem Wort es an der Stelle steht
  const [wording, setWording] = useState<{ state: BrowserState; entity: Entity } | null>(null);
  // sofort gesetzt (der Zustand oben erst beim nächsten Rendern): das Fenster zum Anlegen schließt direkt nach dem Anlegen
  const wordingOpen = useRef(false);
  const [creating, setCreating] = useState<{ typeId: string; name: string; state: BrowserState; all: boolean; span: { keys: string[]; role: SpanRole | null } | null } | null>(null);
  // angeheftete Elemente rechts neben dem Text; offen: das Menü eines davon für die Stelle, die es verknüpft
  const pinnedIds = usePinnedIds(projectId);
  const togglePin = useRefPins((s) => s.toggle);
  const pinned = useMemo(() => pinnedIds.map((id) => ctx.entities.get(id)).filter((e): e is Entity => !!e), [pinnedIds, ctx]);
  const [pinMenu, setPinMenu] = useState<{ entity: Entity; range: Range; text: string; suggest: RefKind; anchor: DOMRect } | null>(null);
  // ruhig schreiben (PF 57): welche Markierungen der Text zeigt
  const markView = useMarkView((s) => s.view);
  const setMarkView = useMarkView((s) => s.setView);
  // Zeitmarke setzen bzw. ändern: die Stelle, ihr Text, die bestehende Marke bzw. der erkannte Wert
  const [timeEditor, setTimeEditor] = useState<{ range: Range; text: string; initial: TimeMarkValue | null; existing: boolean; anchor: { left: number; top: number; bottom: number } } | null>(null);
  // Tastatur und „@“ brauchen den aktuellen Stand, auch aus den Callbacks des Editors heraus
  const refs = useMemo(() => refIndex(ctx), [ctx]);
  const live = useRef({
    onSave,
    refs,
    goTo,
    openBrowser: (_preset?: LinkPreset): void => undefined,
    openAt: (_ed: Editor): void => undefined,
    openTimeEditor: (_at?: Range): void => undefined,
    /** Zeitmarken, deren Text gelöscht wurde (Plugin `timeMarkGuard`) */
    onTimeLost: (_lost: LostTime[]): void => undefined,
    /** Referenz-Browser (oder das Anlegen eines Eintrags daraus) offen */
    browsing: false,
    /** Lage eines „@“, dessen Browser geschlossen wurde: für dieses „@“ öffnet er sich nicht noch einmal */
    dismissed: null as number | null,
  });
  const lastEmitted = useRef(value);

  const editor = useEditor({
    extensions: isField
      ? fieldTextExtensions([Placeholder.configure({ placeholder }), refHighlight(known)], (k) => known.current.has(k))
      : richTextExtensions(
          [
            Placeholder.configure({ placeholder }),
            refHighlight(known),
            linkTarget,
            termMarks(termLive),
            ...(withTerms ? [timeSuggest(timeLive), placeShift(shiftLive), stateHints(stateLive), povSuggestMarks(povLive), sceneSplitMarks(splitLive), suggestKeys] : []),
            // der Wächter läuft vor den übrigen (Priorität): die Widersprüche brauchen seine geänderten Stellen
            timeMarkGuard((lost) => live.current.onTimeLost(lost)),
            ...(withTerms ? [timeConflicts(conflictLive), settingConflictMarks(settingLive), lifeConflictMarks(lifeLive), factConflictMarks(factLive), knowledgeLeakMarks(leakLive)] : []),
            spanFocus,
          ],
          (k) => known.current.has(k),
        ),
    ...(isField ? { content: fieldToDoc(value) } : { content: value, contentType: 'markdown' as const }),
    immediatelyRender: true,
    editorProps: {
      attributes: { class: isField ? 'rt-content rt-field' : 'rt-content md-view rt-page', 'aria-label': ariaLabel, 'aria-multiline': 'true', role: 'textbox', 'data-testid': testId ?? 'doc-rich', spellcheck: 'true', lang: 'de' },
      // Strg/⌘+Klick auf einen Verweis öffnet den Eintrag
      handleClick: (_view, _pos, e) => {
        if (!(e.ctrlKey || e.metaKey)) return false;
        const refKey = (e.target as HTMLElement | null)?.closest?.('[data-ref]')?.getAttribute('data-ref');
        const entity = refKey ? live.current.refs.get(refKey) : undefined;
        if (!entity) return false;
        live.current.goTo({ nodeId: null, entityId: entity.id });
        return true;
      },
      handleKeyDown: (_view, e) => {
        const l = live.current;
        const mod = e.ctrlKey || e.metaKey;
        if (mod && e.key.toLowerCase() === 's') {
          e.preventDefault();
          l.onSave();
          return true;
        }
        if (mod && e.key.toLowerCase() === 'k' && !isField) {
          e.preventDefault();
          l.openBrowser();
          return true;
        }
        // Strg+⇧+6: Zeitmarke setzen bzw. ändern (PLAN.md §16)
        if (mod && e.shiftKey && !e.altKey && e.code === 'Digit6' && !isField) {
          e.preventDefault();
          l.openTimeEditor();
          return true;
        }
        // Strg+⇧+1 … 5: verknüpfen, gleich mit der Art („Ist …“ bzw. „Gehört zu …“ als Rede, Handlung, …)
        const preset = isField ? null : linkPresetOf(e);
        if (preset) {
          e.preventDefault();
          l.openBrowser(preset);
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: ed }) => {
      const text = isField ? docToField(ed.getJSON()) : cleanMarkdown(ed.getMarkdown());
      lastEmitted.current = text;
      onChange(text);
      // „@“ getippt: der Referenz-Browser öffnet sich
      const l = live.current;
      const t = l.browsing ? null : tokenAt(ed);
      if (t && t.from !== l.dismissed) l.openAt(ed);
    },
    onBlur: () => onBlur(),
  });
  const editorRef = useRef<Editor | null>(editor);
  editorRef.current = editor;
  // Test-Hook für E2E (wie `__dpCy`): der Editor des geöffneten Dokuments
  useEffect(() => {
    if (!editor || isField) return;
    const w = window as unknown as { __dpEditor?: Editor };
    w.__dpEditor = editor;
    return () => {
      if (w.__dpEditor === editor) delete w.__dpEditor;
    };
  }, [editor, isField]);

  /** Fokus sofort zurück in den Editor (der Befehl `focus` setzt ihn erst im nächsten Bild – so ginge Getipptes verloren) */
  const refocus = (ed: Editor): void => {
    if (!ed.isDestroyed && !ed.view.hasFocus()) ed.view.focus();
  };

  /** Die Stelle, die gerade verknüpft wird, sichtbar markieren (nach dem laufenden Update des Editors) */
  const markTarget = (ed: Editor, range: Range | null): void => {
    requestAnimationFrame(() => {
      if (!ed.isDestroyed && live.current.browsing) ed.view.dispatch(ed.state.tr.setMeta(targetKey, range));
    });
  };

  /**
   * Was „Verknüpfen“ (bzw. ein angeheftetes Element) verknüpft, samt vorausgewählter Art: schon verknüpft → Nennung; genau ein
   * bestehender Bezug → Bezug; sonst nach der Markierung. Ohne brauchbare Markierung: Hinweis, null.
   */
  const targetAt = (ed: Editor): { range: Range; text: string; linkedKey: string | null; kind: RefKind; span: BrowserState['span'] } | null => {
    const range = linkRange(ed);
    if (range === 'empty' || range === 'blocks') {
      toast.info(range === 'empty' ? de.refBrowser.pickFirst : de.refBrowser.oneBlock);
      return null;
    }
    const doc = ed.state.doc;
    const text = doc.textBetween(range.from, range.to, ' ', '￼');
    const linkedKey = linkedKeyIn(doc, range);
    const existing = spanOver(doc, range.from, range.to);
    const onSpan = existing && existing.from === range.from && existing.to === range.to;
    const kind: RefKind = linkedKey ? 'name' : onSpan || !nameLike(text) ? 'span' : 'name';
    return { range, text, linkedKey, kind, span: onSpan ? { keys: existing.keys, role: existing.role } : null };
  };

  /** Favoriten des Browsers: die Elemente, die im Text schon vorkommen (Nennungen, Bezüge, Verweise), in der Reihenfolge ihres ersten Vorkommens */
  const favoritesNow = (): string[] => [...new Set(mentionsIn(lastEmitted.current).flatMap((m) => refs.get(m.key)?.id ?? []))];

  /** Referenz-Browser für die Markierung (bzw. das Wort am Cursor) öffnen; mit `preset` gleich mit dieser Art (Tastenkürzel) */
  const openBrowser = (preset?: LinkPreset): void => {
    const ed = editorRef.current;
    if (!ed || isField) return;
    const found = targetAt(ed);
    if (!found) return;
    const t = preset ? { ...found, kind: preset.kind, role: preset.kind === 'span' ? preset.role : undefined } : found;
    const { range, text } = t;
    const start = ed.view.coordsAtPos(range.from);
    const end = ed.view.coordsAtPos(range.to);
    ed.view.dispatch(ed.state.tr.setMeta(targetKey, range));
    live.current.browsing = true;
    const anchor = { left: start.left, top: Math.min(start.top, end.top), bottom: Math.max(start.bottom, end.bottom) };
    setBrowser({ ...t, others: otherPlaces(ed.state.doc, text, range, ed.state.schema.marks.refLink!), anchor, at: null, query: '', kinds: true, favorites: favoritesNow() });
  };

  /** Bezug an der Randlinie ändern: der Browser für die ganze Stelle, als „Gehört zu …“ */
  const openSpan = (s: SpanRange): void => {
    const ed = editorRef.current;
    if (!ed || isField) return;
    const range = { from: s.from, to: s.to };
    const start = ed.view.coordsAtPos(range.from);
    const end = ed.view.coordsAtPos(range.to);
    ed.view.dispatch(ed.state.tr.setMeta(targetKey, range));
    live.current.browsing = true;
    const text = ed.state.doc.textBetween(range.from, range.to, ' ', '￼');
    const anchor = { left: start.left, top: Math.min(start.top, end.top), bottom: Math.max(start.bottom, end.bottom) };
    setBrowser({ range, text, linkedKey: null, others: [], anchor, at: null, query: '', kind: 'span', kinds: true, span: { keys: s.keys, role: s.role }, favorites: favoritesNow() });
  };

  /**
   * „@“ getippt: Direkt an einem Wort („Sarah@“) verknüpft der Referenz-Browser dieses Wort mit dem gewählten Element. Allein stehend
   * („Sarah @“, am Zeilenanfang) fragt nach der Wahl des Elements ein zweites Fenster, mit welchem Wort es an der Stelle steht (Name,
   * Vorkommen oder ein neues). Was nach dem „@“ schon getippt ist, steht in der Suche des Browsers.
   */
  const openAt = (ed: Editor): void => {
    const t = tokenAt(ed);
    if (!t) return;
    const doc = ed.state.doc;
    // nur im selben Absatz: das Ende einer Überschrift davor ist kein „Wort vor dem @“
    const $at = doc.resolve(t.from);
    const before = $at.parent.textBetween(Math.max(0, $at.parentOffset - 60), $at.parentOffset, undefined, '￼');
    const word = new RegExp(`(${WORD}+)$`, 'u').exec(before)?.[1];
    const range = word ? { from: t.from - word.length, to: t.from } : null;
    const start = ed.view.coordsAtPos(range ? range.from : t.from);
    const end = ed.view.coordsAtPos(t.to);
    markTarget(ed, range ?? { from: t.from, to: t.to });
    live.current.browsing = true;
    setBrowser({
      range,
      text: word ?? '',
      linkedKey: range ? linkedKeyIn(doc, range) : null,
      others: range && word ? otherPlaces(doc, word, range, ed.state.schema.marks.refLink!) : [],
      anchor: { left: start.left, top: Math.min(start.top, end.top), bottom: Math.max(start.bottom, end.bottom) },
      at: t.from,
      query: t.query,
      kind: 'name',
      kinds: false,
      span: null,
      favorites: favoritesNow(),
    });
  };
  /**
   * Zeitmarke setzen bzw. ändern (Knopf „Zeit“, Strg+⇧+6, PLAN.md §16): für die Markierung, sonst für die Zeitmarke bzw. die mögliche
   * Zeitangabe am Cursor. Vorbelegt mit der bestehenden Marke bzw. dem, was im Text erkannt wird.
   */
  const openTimeEditor = (at?: Range): void => {
    const ed = editorRef.current;
    if (!ed || isField) return;
    // eine gegebene Stelle (nach „Neue Zeit setzen“) wie eine Markierung behandeln
    const sel = ed.state.selection;
    const { from, to, empty, $from, $to } = at ? { from: at.from, to: at.to, empty: false, $from: ed.state.doc.resolve(at.from), $to: ed.state.doc.resolve(at.to) } : sel;
    if (!empty && !$from.sameParent($to)) return void toast.info(de.refBrowser.oneBlock);
    const existing = timeOver(ed.state.doc, from, to);
    const hit = empty ? timeSuggestKey.getState(ed.state)?.hits.find((h) => h.from <= from && from < h.to) : undefined;
    let range: Range | null = existing ? { from: existing.from, to: existing.to } : hit ? { from: hit.from, to: hit.to } : empty ? null : { from, to };
    if (!range) return void toast.info(de.timeMarks.pickFirst);
    // Leerzeichen am Rand gehören nicht zur Stelle
    const raw = ed.state.doc.textBetween(range.from, range.to, ' ', '￼');
    range = { from: range.from + (raw.length - raw.trimStart().length), to: range.to - (raw.length - raw.trimEnd().length) };
    if (range.from >= range.to) return void toast.info(de.timeMarks.pickFirst);
    const text = ed.state.doc.textBetween(range.from, range.to, ' ', '￼');
    const recognized = parseTimeInput(text);
    const initial: TimeMarkValue | null = existing
      ? { value: existing.value, precision: existing.precision, approx: existing.approx, role: existing.role }
      : hit
        ? { value: hit.value, precision: hit.precision, approx: hit.approx, role: hit.role }
        : recognized && { value: recognized.value, precision: recognized.precision, approx: recognized.approx, role: recognized.role };
    const start = ed.view.coordsAtPos(range.from);
    const end = ed.view.coordsAtPos(range.to);
    ed.view.dispatch(ed.state.tr.setMeta(targetKey, range));
    setTimeEditor({ range, text, initial, existing: !!existing, anchor: { left: start.left, top: Math.min(start.top, end.top), bottom: Math.max(start.bottom, end.bottom) } });
  };
  const closeTimeEditor = (back: boolean): void => {
    const ed = editorRef.current;
    const t = timeEditor;
    setTimeEditor(null);
    if (!ed || ed.isDestroyed) return;
    if (back && t) ed.chain().focus().setTextSelection(t.range).setMeta(targetKey, null).run();
    else ed.view.dispatch(ed.state.tr.setMeta(targetKey, null));
  };
  /** Zeitmarke an die Stelle setzen (eine bestehende wird ersetzt); danach steht der Cursor hinter der Stelle */
  const setTimeMark = (range: Range, v: TimeMarkValue): void => {
    const ed = editorRef.current;
    if (!ed || ed.isDestroyed) return;
    ed.chain()
      .focus()
      .setTextSelection(range)
      .unsetMark('timeMark')
      .setMark('timeMark', { value: v.value, precision: v.precision, approx: v.approx, role: v.role })
      .setTextSelection(range.to)
      .setMeta(targetKey, null)
      .setMeta(timeGuardKey, { clear: [range.from, range.to] })
      .run();
    const text = ed.state.doc.textBetween(range.from, range.to, ' ', '￼');
    toast.ok(de.timeMarks.applied(text.length > 40 ? `${text.slice(0, 38)}…` : text));
  };
  /** Text einer Zeitmarke ganz gelöscht: mit ihm ist die Marke weg – Hinweis, eine neue Zeit zu setzen (geändert bleibt sie, §16d) */
  const onTimeLost = (lost: LostTime[]): void => {
    const l = lost[lost.length - 1];
    if (!l) return;
    const short = l.text.length > 40 ? `${l.text.slice(0, 38)}…` : l.text;
    toast.action(de.timeMarks.lostDeleted(short), de.timeMarks.setNew, () => live.current.openTimeEditor());
  };
  /** Mögliche Zeitangabe: ✓ setzt die Zeitmarke, ✕ merkt die Stelle als „hier nicht“ */
  const timeAct = (act: TimeAct, hit: TimeSuggestion): void => {
    if (act === 'ignore') return void onIgnore?.({ term: hit.text, before: hit.before, after: hit.after });
    setTimeMark({ from: hit.from, to: hit.to }, { value: hit.value, precision: hit.precision, approx: hit.approx, role: hit.role });
  };
  live.current = { ...live.current, onSave, refs, goTo, openBrowser, openAt, openTimeEditor, onTimeLost, browsing: browser !== null || creating !== null || wording !== null || pinMenu !== null || timeEditor !== null };

  /** Möglicher Verweis: ✓ verknüpft mit dem Element (gehört das Wort mehreren, wählt man im Referenz-Browser), ✕ ignoriert die Stelle */
  const termAct = (act: TermAct, at: TermHit): void => {
    const ed = editorRef.current;
    const hit = ed && !ed.isDestroyed ? termKey.getState(ed.state)?.hits.find((h) => h.from === at.from) : undefined;
    if (!ed || !hit) return;
    if (act === 'ignore') return void onIgnore?.({ term: hit.term, before: hit.before, after: hit.after });
    const entity = hit.entityIds.length === 1 ? ctx.entities.get(hit.entityIds[0]!) : undefined;
    if (entity) ed.view.dispatch(ed.state.tr.addMark(hit.from, hit.to, ed.state.schema.marks.refLink!.create({ key: entity.refKey })));
    else {
      ed.chain().focus().setTextSelection({ from: hit.from, to: hit.to }).run();
      openBrowser();
    }
  };
  /** Alle Stellen verknüpfen, die eindeutig zu einem Element gehören */
  const acceptAll = (): void => {
    const ed = editorRef.current;
    if (!ed || ed.isDestroyed) return;
    const tr = ed.state.tr;
    for (const h of termKey.getState(ed.state)?.hits ?? []) {
      const entity = h.entityIds.length === 1 ? ctx.entities.get(h.entityIds[0]!) : undefined;
      if (entity) tr.addMark(h.from, h.to, ed.state.schema.marks.refLink!.create({ key: entity.refKey }));
    }
    if (tr.docChanged) ed.view.dispatch(tr);
  };
  const entityName = (id: string): string => {
    const e = ctx.entities.get(id);
    return (e && titleOf(e, ctx.types.get(e.typeId))) || (e ? `@${e.refKey}` : '');
  };
  /** Name zu einem Verweisschlüssel (unbekannt: der Schlüssel) */
  const keyName = (key: string): string => entityName(refs.get(key)?.id ?? '') || `@${key}`;
  /** Möglicher Ortswechsel: ✓ macht den Ort zum Schauplatz der Szene, ✕ merkt „kein Ortswechsel“ für diesen Wechsel */
  const shiftAct = (act: ShiftAct, hit: ShiftHit): void => {
    if (act === 'ignore') return void onIgnore?.(shiftIgnored(hit.fromKey, hit.key));
    if (!timeScene) return;
    void actions.updateNode(timeScene.id, { fields: { place: hit.entityId } });
    toast.ok(de.places.made(entityName(hit.entityId)));
  };
  const setting = timeScene ? placeOf(ctx, timeScene) : null;
  shiftLive.current = {
    ...shiftLive.current,
    on: withTerms && !!timeScene,
    placeOf: (key) => {
      const e = refs.get(key);
      return e && isPlaceEntity(ctx, e.id) ? e.id : null;
    },
    setting,
    ignored,
    review: withTerms && review,
    act: shiftAct,
    title: (h) => de.places.shiftTitle(entityName(h.fromId), entityName(h.entityId)),
  };
  /** Text gegen Angabe: ✓ übernimmt Schauplatz bzw. Perspektive aus dem Text, ✕ merkt „ist Absicht“ im Dokument (Head-Hopping: nur ✕) */
  const settingAct = (act: SettingAct, hit: SettingHit): void => {
    const c = hit.conflict;
    const t = de.settingConflicts;
    if (act === 'ignore') {
      onIgnore?.(settingConflictIgnore(ctx, c));
      return void toast.ok(t.intendedDone);
    }
    if (!timeScene || c.kind === 'hop') return;
    const id = c.kind === 'place' ? c.at.entityId : c.thinkers[0]!;
    void actions.updateNode(timeScene.id, { fields: { [c.kind === 'place' ? 'place' : 'pov']: id } });
    toast.ok(c.kind === 'place' ? t.placeDone(entityName(id)) : t.povDone(entityName(id)));
  };
  settingLive.current = {
    ...settingLive.current,
    on: withTerms && !!timeScene,
    ctx,
    scene: timeScene,
    entityOf: (key) => refs.get(key.toLowerCase())?.id ?? null,
    placeOf: shiftLive.current.placeOf,
    ignored,
    review: withTerms && review,
    act: settingAct,
    title: (h) => {
      const c = h.conflict;
      const t = de.settingConflicts;
      if (c.kind === 'hop') return t.hopTitle(c.thinkers.map(entityName).join(', '), entityName(c.settingId));
      return c.kind === 'place' ? t.placeTitle(entityName(c.at.entityId), entityName(c.settingId)) : t.povTitle(c.thinkers.map(entityName).join(', '), entityName(c.settingId));
    },
    hitLabels: (h) => {
      const c = h.conflict;
      const t = de.settingConflicts;
      if (c.kind === 'hop') return { accept: null, ignore: t.intended };
      return { accept: c.kind === 'place' ? t.acceptPlace(entityName(c.at.entityId)) : t.acceptPov(entityName(c.thinkers[0]!)), ignore: t.intended };
    },
  };
  /** Perspektive aus dem Text: ✓ macht die Person zur Perspektive der Szene, ✕ merkt „ohne Perspektive“ im Dokument */
  const povAct = (act: SuggestAct, hit: PovHit): void => {
    if (act === 'ignore') {
      onIgnore?.(povSuggestionIgnore(ctx, hit));
      return void toast.ok(de.povSuggest.ignored);
    }
    if (!timeScene) return;
    void actions.updateNode(timeScene.id, { fields: { pov: hit.entityId } });
    toast.ok(de.settingConflicts.povDone(entityName(hit.entityId)));
  };
  /** Lebt nicht: ✕ merkt „ist Absicht“ für die Figur im Dokument, der weitere Ausweg zeigt die Szene von Tod bzw. Geburt */
  const lifeAct = (act: SuggestAct, hit: LifeHit): void => {
    const c = hit.conflict;
    if (act === 'ignore') {
      onIgnore?.(lifeIgnore(ctx, c.entityId, 'text'));
      return void toast.ok(de.lifeConflicts.intendedDone);
    }
    if (act === 'other' && c.eventSceneId) goTo({ nodeId: c.eventSceneId });
  };
  lifeLive.current = {
    ...lifeLive.current,
    on: withTerms && !!timeScene,
    ctx,
    scene: timeScene,
    entityOf: settingLive.current.entityOf,
    ignored,
    review: withTerms && review,
    act: lifeAct,
    title: (h) => de.lifeConflicts.title(entityName(h.conflict.entityId), h.conflict.state, h.conflict.prior ? `in ${priorText(h.conflict.prior)}` : `in „${sceneTitle(h.conflict.eventSceneId)}“`),
    // in einer früheren Episode: keine Szene hier
    other: (h) =>
      h.conflict.eventSceneId ? { label: h.conflict.state === 'dead' ? de.lifeConflicts.toDeath : de.lifeConflicts.toBirth, title: de.lifeConflicts.toScene(sceneTitle(h.conflict.eventSceneId)) } : null,
  };
  /** Wissensleck: ✕ merkt „ist Absicht“ für Figur und Gewusstes im Dokument, der weitere Ausweg öffnet die Stelle, an der sie es erfährt */
  const leakAct = (act: SuggestAct, hit: LeakHit): void => {
    if (act === 'ignore') {
      onIgnore?.(leakIgnore(ctx, hit));
      return void toast.ok(de.knowledgeLeaks.intendedDone);
    }
    if (act !== 'other') return;
    const scene = ctx.nodes.get(hit.learnedIn);
    const at = scene ? learningsIn(ctx, scene).find((x) => x.role === 'erfährt' && x.knowers.includes(hit.knower) && x.about.includes(hit.about)) : undefined;
    goTo({ nodeId: hit.learnedIn, ...(at ? { docId: at.docId } : {}) });
  };
  /**
   * Alter, Merkmal bzw. benutzter Gegenstand passt nicht (AP-40, AP-42): ✕ merkt „ist Absicht“ im Dokument, der weitere Ausweg zeigt die
   * Geburt bzw. die Szene, seit der das Merkmal bzw. der Besitz gilt
   */
  const factAct = (act: SuggestAct, hit: FactHit): void => {
    if (act === 'ignore') {
      onIgnore?.(hit.kind === 'age' ? ageIgnore(ctx, hit.conflict) : hit.kind === 'trait' ? traitIgnore(ctx, hit.conflict) : itemUseIgnore(ctx, hit.conflict));
      return void toast.ok(de.ageConflicts.intendedDone);
    }
    const target = factScene(hit);
    if (act === 'other' && target) goTo({ nodeId: target });
  };
  const factScene = (h: FactHit): string | null => (h.kind === 'age' ? h.conflict.bornSceneId || null : h.kind === 'trait' ? h.conflict.sinceSceneId || null : h.conflict.sinceSceneId || null);
  const holderText = (c: { holder: string | null }): string => (c.holder === null ? de.states.lost : stateRoleOf(ctx, c.holder) === 'place' ? de.states.at(entityName(c.holder)) : de.states.with(entityName(c.holder)));
  factLive.current = {
    ...factLive.current,
    on: withTerms && !!timeScene,
    ctx,
    scene: timeScene,
    entityOf: settingLive.current.entityOf,
    ignored,
    review: withTerms && review,
    act: factAct,
    title: (h) =>
      h.kind === 'age'
        ? de.ageConflicts.title(entityName(h.conflict.entityId), h.conflict.years, h.conflict.actual, h.conflict.bornPrior ? priorText(h.conflict.bornPrior) : `„${sceneTitle(h.conflict.bornSceneId)}“`)
        : h.kind === 'trait'
          ? de.ageConflicts.traitTitle(entityName(h.conflict.entityId), h.conflict.said, h.conflict.trait, h.conflict.actual)
          : de.ageConflicts.itemTitle(entityName(h.conflict.personId), entityName(h.conflict.itemId), holderText(h.conflict)),
    other: (h) => {
      const target = factScene(h);
      if (!target) return null;
      return { label: h.kind === 'age' ? de.ageConflicts.toBirth : de.ageConflicts.toSince, title: de.lifeConflicts.toScene(sceneTitle(target)) };
    },
  };
  leakLive.current = {
    ...leakLive.current,
    on: withTerms && !!timeScene,
    ctx,
    scene: timeScene,
    entityOf: settingLive.current.entityOf,
    ignored,
    review: withTerms && review,
    act: leakAct,
    title: (h) => de.knowledgeLeaks.title(entityName(h.knower), entityName(h.about), h.role, sceneTitle(h.learnedIn)),
    other: (h) => ({ label: de.knowledgeLeaks.toLearning, title: de.knowledgeLeaks.toLearningHint(sceneTitle(h.learnedIn)) }),
  };
  /**
   * Szene teilen (PF 51): der Teil ab der Stelle wird eine neue Szene direkt hinter dieser (im selben Kapitel; Zeit und Ort ergeben sich aus
   * ihrem Text), dieser Draft behält den Teil davor; ein Szenentrenner fällt weg. ✕ merkt „nicht teilen“ an der Stelle.
   */
  const splitAct = (act: SuggestAct, hit: SplitHit): void => {
    if (act === 'ignore') {
      onIgnore?.(splitIgnore(hit.key));
      return void toast.ok(de.sceneSplit.ignored);
    }
    if (act === 'accept') void splitScene(hit);
  };
  const splitScene = async (hit: SplitHit): Promise<void> => {
    const ed = editor;
    const scene = timeScene;
    if (!ed || !scene || !ed.markdown) return;
    const doc = ed.state.doc;
    const before = doc.cut(0, hit.cutFrom);
    const rest = cleanMarkdown(ed.markdown.serialize(doc.cut(hit.cutTo).toJSON()));
    // direkt hinter dieser Szene im selben Kapitel
    const siblings = narrativeOrder(ctx).chapters.find((c) => c.chapter.id === scene.chapterId)?.scenes ?? [];
    const i = siblings.findIndex((s) => s.scene.id === scene.id);
    const rank = scene.chapterId && scene.rank && i >= 0 ? rankBetween(scene.rank, siblings[i + 1]?.scene.rank ?? null) : undefined;
    const title = String(scene.fields.title ?? '').trim();
    const cs = await actions.createNode({ mode: 'scene', chapterId: scene.chapterId ?? null, ...(rank ? { rank } : {}), fields: { title: de.sceneSplit.newTitle(title), status: String(scene.fields.status ?? 'Idee') } });
    const created = cs?.nodes.find((n) => isScene(n));
    const draft = created?.documents.find((d) => d.locked) ?? created?.documents[0];
    if (!created || !draft) return;
    if (!(await actions.updateDocument(created.id, draft.id, { text: rest }))) return;
    ed.commands.setContent(before.toJSON());
    toast.action(de.sceneSplit.done(de.sceneSplit.newTitle(title)), de.sceneSplit.open, () => goTo({ nodeId: created.id, docId: draft.id }));
  };
  splitLive.current = {
    ...splitLive.current,
    on: withTerms && !!timeScene,
    scene: timeScene,
    ignored,
    review: withTerms && review,
    act: splitAct,
    title: (h) => (h.kind === 'separator' ? de.sceneSplit.titleSeparator : de.sceneSplit.titleTime),
  };
  povLive.current = {
    ...povLive.current,
    on: withTerms && !!timeScene,
    ctx,
    scene: timeScene,
    entityOf: settingLive.current.entityOf,
    ignored,
    review: withTerms && review,
    act: povAct,
    title: (h) => (h.firstPerson ? de.povSuggest.titleFirstPerson(entityName(h.entityId)) : de.povSuggest.title(entityName(h.entityId))),
  };
  /** Vorschlag als Änderung (Schlüssel → Elemente); null, wenn ein Element fehlt */
  const changeOf = (h: StateHint): Omit<StateChange, 'id'> | null => {
    const entityId = refs.get(h.key)?.id;
    const value = h.kind === 'verfassung' ? (isCondition(h.value) ? h.value : null) : h.value === null ? null : (refs.get(h.value)?.id ?? null);
    if (!entityId || (h.value !== null && value === null)) return null;
    // Besitz: wer ihn weitergibt bzw. verliert, soweit der Satz es sagt (für die Prüfung, ob er ihn da hat)
    const from = h.giver ? refs.get(h.giver)?.id : undefined;
    return { entityId, kind: h.kind, value, ...(from && from !== entityId ? { from } : {}) };
  };
  /** Mögliche Zustandsänderung: ✓ hält sie in der Szene fest (mit dem Satz als Beleg), ✕ merkt „keine Änderung“ im Dokument */
  const stateAct = (act: StateAct, h: StateHint): void => {
    if (act === 'ignore') return void onIgnore?.(stateIgnored(h));
    const c = changeOf(h);
    if (!timeScene || !c) return;
    void actions.updateNode(timeScene.id, { changes: [...changesOf(timeScene), { ...c, id: crypto.randomUUID(), quote: h.quote.slice(0, 400) }] });
    toast.ok(de.states.done(entityName(c.entityId), changeText(ctx, c)));
  };
  stateLive.current = {
    ...stateLive.current,
    on: withTerms && !!timeScene,
    roleOf: (key) => {
      const e = refs.get(key);
      return e ? stateRoleOf(ctx, e.id) : null;
    },
    isSet: (h) => {
      const c = changeOf(h);
      return !!c && !!timeScene && changesOf(timeScene).some((x) => sameChange(x, c));
    },
    ignored,
    review: withTerms && review,
    act: stateAct,
    title: (h, set) => {
      const c = changeOf(h);
      const what = c ? changeText(ctx, c) : h.word;
      return set ? de.states.setTitle(keyName(h.key), what) : de.states.hintTitle(keyName(h.key), what);
    },
  };
  /** Zeit einer Szene in Worten („03.03.1823“, „ca. 03.03.1823 07:00 – 04.03.1823“) */
  const when = (t: SceneTime | null | undefined): string => (t?.start ? formatSceneTime(t.start, t) + (t.end ? ` – ${formatSceneTime(t.end, t)}` : '') : '');
  const sceneTitle = (id: string): string => {
    const n = ctx.nodes.get(id);
    return n ? displayOf(ctx, n).title : '';
  };
  const meaningOf = (m: { value: string; precision: TimePrecision | null; approx: boolean; role?: MarkRole | null }): string => describeTimeMark(m.value, m.precision, m.approx, m.role ?? null);
  /**
   * Zeitmarke, die nicht mehr stimmt: ✓ übernimmt den Vorschlag (neuer Wortlaut bzw. Wert; bei der Reihenfolge zählt die Zeit danach ab der
   * Szene davor), ✕ merkt „ist Absicht“; der weitere Ausweg setzt die Szene auf die Zeit aus dem Text, zählt ab der Szene davor bzw. öffnet
   * das Setzen der Zeit.
   */
  const conflictAct = (act: ConflictAct, hit: ConflictHit): void => {
    const ed = editorRef.current;
    if (!ed || ed.isDestroyed) return;
    const c = hit.conflict;
    const t = de.timeConflicts;
    if (act === 'ignore') {
      if (c.kind === 'edited') return void ed.view.dispatch(ed.state.tr.setMeta(timeGuardKey, { clear: [hit.mark.from, hit.mark.to] }));
      onIgnore?.(timeConflictIgnore(c, timeScene, markAt(hit.mark)));
      return void toast.ok(t.intendedDone);
    }
    if (act === 'other') {
      if (c.kind === 'edited') return live.current.openTimeEditor({ from: hit.mark.from, to: hit.mark.to });
      // Wochentag: das Datum rückt auf den nächsten Tag mit dem genannten Wochentag
      if (c.kind === 'weekday') return void (c.dateFix && applyFix(hit, c.dateFix, false));
      if (!timeScene?.time) return;
      if (c.kind === 'moved') {
        void actions.updateNode(timeScene.id, { time: c.textTime });
        return void toast.ok(t.adopted);
      }
      if (c.kind === 'order' && c.previousId) {
        void actions.updateNode(timeScene.id, { time: { ...timeScene.time, anchor: { scene: c.previousId } } });
        toast.ok(t.anchored(sceneTitle(c.previousId)));
      }
      return;
    }
    if (c.kind === 'edited' || !c.fix) return;
    applyFix(hit, c.fix, c.kind === 'wording');
    if (c.kind === 'order' && c.fix.anchor && timeScene?.time) void actions.updateNode(timeScene.id, { time: { ...timeScene.time, anchor: { scene: c.fix.anchor } } });
  };
  /** Vorschlag an einer Zeitmarke übernehmen: Wortlaut – die Marke deckt danach auch den dazugetippten Text; sonst bekommt sie den neuen Text */
  const applyFix = (hit: ConflictHit, fix: MarkFix, wording: boolean): void => {
    const ed = editorRef.current;
    if (!ed || ed.isDestroyed) return;
    const from = wording ? hit.from : hit.mark.from;
    let to = wording ? hit.to : hit.mark.to;
    const type = ed.state.schema.marks.timeMark!;
    const tr = ed.state.tr;
    if (!wording && fix.text !== hit.mark.text) {
      tr.insertText(fix.text, from, to);
      to = from + fix.text.length;
    }
    tr.removeMark(from, to, type)
      .addMark(from, to, type.create({ value: fix.value, precision: fix.precision, approx: fix.approx, role: fix.role }))
      .setMeta(timeGuardKey, { clear: [from, to] });
    ed.view.dispatch(tr);
    toast.ok(wording ? de.timeConflicts.valueSet : de.timeConflicts.applied(fix.text));
  };
  conflictLive.current = {
    ...conflictLive.current,
    ctx,
    scene: timeScene,
    ignored,
    review: withTerms && review,
    act: conflictAct,
    title: (h) => {
      const c = h.conflict;
      const t = de.timeConflicts;
      if (c.kind === 'edited') return t.edited(meaningOf(h.mark));
      if (c.kind === 'wording') return t.wording(meaningOf(c.fix), meaningOf(h.mark));
      if (c.kind === 'weekday') return t.weekday(c.said, c.actual);
      const proposal = !c.fix
        ? t.noProposal
        : c.kind === 'order'
          ? t.proposalTimeStays(c.fix.text, sceneTitle(c.fix.anchor ?? ''))
          : c.fix.text !== h.mark.text
            ? t.proposal(c.fix.text)
            : t.proposalValue(meaningOf(c.fix));
      const reason = c.kind === 'moved' ? t.moved(when(timeScene?.time), when(c.textTime)) : t.order(sceneTitle(c.anchorId), c.previousId ? sceneTitle(c.previousId) : null);
      return `${reason} ${proposal}`;
    },
    hitLabels: (h) => {
      const c = h.conflict;
      const t = de.timeConflicts;
      if (c.kind === 'edited') return { accept: null, ignore: t.fine };
      if (c.kind === 'wording') return { accept: t.acceptValue(meaningOf(c.fix)), ignore: t.intended };
      if (c.kind === 'weekday') return { accept: t.acceptWeekday(c.actual), ignore: t.intended };
      return { accept: !c.fix ? null : c.fix.text !== h.mark.text ? t.acceptText(c.fix.text) : t.acceptValue(meaningOf(c.fix)), ignore: t.intended };
    },
    other: (h) => {
      const c = h.conflict;
      const t = de.timeConflicts;
      if (c.kind === 'moved') return { label: t.fromText, title: t.fromTextHint(when(c.textTime)) };
      if (c.kind === 'order' && c.previousId) return { label: t.countFrom, title: t.countFromHint(sceneTitle(c.previousId)) };
      if (c.kind === 'edited') return { label: t.setTime, title: t.setTimeHint };
      if (c.kind === 'weekday' && c.dateFix) return { label: t.weekdayDate, title: t.weekdayDateHint(c.dateFix.text) };
      return null;
    },
  };
  termLive.current = { ...termLive.current, index: terms, ignored, review: withTerms && review, act: termAct, title: (h: TermHit) => de.terms.title(h.entityIds.map(entityName)) };
  timeLive.current = { ...timeLive.current, ignored, review: withTerms && review, act: timeAct, title: (h: TimeSuggestion) => de.timeMarks.suggestTitle(describeTimeMark(h.value, h.precision, h.approx, h.role)) };

  /** Browser zu: mit „@“ geöffnet bleibt das „@…“ stehen (der Cursor dahinter), sonst kehrt der Cursor an die Stelle zurück */
  const closeBrowser = (back = false, state: BrowserState | null = browser): void => {
    const ed = editorRef.current;
    if (ed && !ed.isDestroyed) {
      if (state?.at != null) {
        live.current.dismissed = state.at;
        (back ? ed.chain().focus().setTextSelection(tokenEnd(ed.state.doc, state.at)) : ed.chain()).setMeta(targetKey, null).run();
      } else if (back && state?.range) ed.chain().focus().setTextSelection(state.range).setMeta(targetKey, null).run();
      else ed.view.dispatch(ed.state.tr.setMeta(targetKey, null));
      if (back) refocus(ed);
    }
    live.current.browsing = false;
    setBrowser(null);
  };

  /**
   * Stelle (und auf Wunsch die gleichlautenden weiteren) mit dem Element verknüpfen; mit „@“ geöffnet verschwindet das „@…“. Allein
   * stehend (ohne Wort direkt davor) geht es erst weiter zur Wortwahl. Danach steht der Cursor hinter der Stelle.
   */
  const link = (state: BrowserState, entity: Entity, refKey: string, all: boolean): void => {
    const ed = editorRef.current;
    if (!ed || ed.isDestroyed) return;
    if (!state.range) {
      live.current.browsing = true;
      wordingOpen.current = true;
      setWording({ state, entity });
      return;
    }
    const doc = ed.state.doc;
    let chain = ed.chain().focus().setTextSelection(state.range).setMark('refLink', { key: refKey });
    if (all) for (const r of state.others) chain = chain.setTextSelection(r).setMark('refLink', { key: refKey });
    chain = state.at === null ? chain.setTextSelection(state.range.to) : chain.deleteRange({ from: state.at, to: tokenEnd(doc, state.at) }).setTextSelection(state.at);
    chain.setMeta(targetKey, null).run();
    refocus(ed);
    live.current.browsing = false;
    const name = titleOf(entity, ctx.types.get(entity.typeId)) || entity.refKey;
    toast.ok(state.text ? de.refBrowser.linked(state.text.length > 40 ? `${state.text.slice(0, 38)}…` : state.text, name) : de.refBrowser.inserted(name));
  };

  /**
   * Wortwahl: Das gewählte Wort ersetzt das „@…“ (samt einer schon getippten Klammer davor bzw. dahinter) und ist mit dem Element
   * verknüpft; ein neues Wort lernt das Element beim Speichern als Vorkommen. Dahinter ein Leerzeichen, falls nicht schon eines bzw. ein
   * Satzzeichen folgt.
   */
  const insertWord = (state: BrowserState, entity: Entity, word: string): void => {
    const ed = editorRef.current;
    setWording(null);
    wordingOpen.current = false;
    live.current.browsing = false;
    if (!ed || ed.isDestroyed || state.at === null) return;
    const doc = ed.state.doc;
    const from = state.at > 0 && doc.textBetween(state.at - 1, state.at) === '[' ? state.at - 1 : state.at;
    let to = tokenEnd(doc, state.at);
    if (to < doc.content.size && doc.textBetween(to, to + 1) === ']') to += 1;
    // das Zeichen dahinter im selben Absatz (am Absatzende: keins – dann kommt ein Leerzeichen, damit man weiterschreiben kann)
    const $to = doc.resolve(to);
    const next = $to.parent.textBetween($to.parentOffset, Math.min($to.parent.content.size, $to.parentOffset + 1), undefined, '￼');
    const space = /^[\s.,;:!?)"“”'»…]/.test(next) ? '' : ' ';
    ed.chain()
      .focus()
      .insertContentAt({ from, to }, [{ type: 'text', text: word, marks: [{ type: 'refLink', attrs: { key: entity.refKey } }] }, ...(space ? [{ type: 'text', text: space }] : [])])
      .setMeta(targetKey, null)
      .run();
    refocus(ed);
    const name = titleOf(entity, ctx.types.get(entity.typeId)) || entity.refKey;
    toast.ok(de.refBrowser.linked(word.length > 40 ? `${word.slice(0, 38)}…` : word, name));
  };
  /** Wörter zur Wahl: der Name und die Vorkommen (Foundationals haben keine – dann der Name bzw. der Schlüssel) */
  const wordsOf = (entity: Entity): string[] => {
    const terms = termsOf(ctx, entity);
    return terms.length > 0 ? terms : [entityName(entity.id) || entity.refKey];
  };

  /** ✕ am Verweis: Nennung lösen (das Wort bleibt), ein allein stehender Verweis wird zum Namen; ✕ am Schild: Bezug lösen */
  const removeRef = (ref: RemovableRef | { kind: 'span'; range: Range }): void => {
    const ed = editorRef.current;
    if (!ed || ed.isDestroyed) return;
    const { from, to } = ref.range;
    const tr = ed.state.tr;
    if (ref.kind === 'link') tr.removeMark(from, to, ed.state.schema.marks.refLink!);
    else if (ref.kind === 'span') tr.removeMark(from, to, ed.state.schema.marks.refSpan!);
    else tr.insertText(refs.has(ref.key) ? keyName(ref.key) : ref.key, from, to);
    ed.view.dispatch(tr);
  };

  /** Bezug setzen (andere Elemente bzw. Art ersetzen einen bestehenden an der Stelle); ohne Elemente: lösen */
  const assignSpan = (state: BrowserState, keys: string[], role: SpanRole | null): void => {
    const ed = editorRef.current;
    if (!ed || ed.isDestroyed || !state.range) return;
    const chain = ed.chain().focus().setTextSelection(state.range);
    (keys.length > 0 ? chain.setMark('refSpan', { keys, role }) : chain.unsetMark('refSpan')).setTextSelection(state.range.to).setMeta(targetKey, null).run();
    refocus(ed);
    live.current.browsing = false;
    const short = state.text.length > 40 ? `${state.text.slice(0, 38)}…` : state.text;
    toast.ok(keys.length > 0 ? de.refBrowser.spanned(short, keys.map(keyName).join(', ')) : de.refBrowser.unspanned);
  };

  /** Angeheftetes Element angeklickt: Menü für die Markierung (bzw. das Wort am Cursor) öffnen; noch einmal geklickt: schließen */
  const openPin = (entity: Entity, anchor: DOMRect): void => {
    const ed = editorRef.current;
    if (!ed || ed.isDestroyed) return;
    if (pinMenu?.entity.id === entity.id) return closePin();
    const t = targetAt(ed);
    if (!t) return void closePin();
    ed.view.dispatch(ed.state.tr.setMeta(targetKey, t.range));
    setPinMenu({ entity, range: t.range, text: t.text, suggest: t.kind, anchor });
  };
  const closePin = (): void => {
    const ed = editorRef.current;
    if (ed && !ed.isDestroyed) ed.view.dispatch(ed.state.tr.setMeta(targetKey, null));
    setPinMenu(null);
  };
  /**
   * Im Menü gewählt: „Ist …“ verknüpft die Stelle mit dem Element, „Gehört zu …“ ordnet sie ihm zu (ein Bezug genau auf dieser Stelle
   * bekommt es dazu, mit der gewählten Art). Die Stelle folgt Änderungen am Text, solange das Menü offen ist (Markierung der Stelle).
   */
  const pickPin = (choice: PinChoice): void => {
    const ed = editorRef.current;
    const m = pinMenu;
    setPinMenu(null);
    if (!ed || ed.isDestroyed || !m) return;
    const mark = targetKey.getState(ed.state)?.find()[0];
    const range = mark ? { from: mark.from, to: mark.to } : m.range;
    const text = ed.state.doc.textBetween(range.from, range.to, ' ', '￼');
    const state: BrowserState = { range, text, linkedKey: null, others: [], anchor: { left: 0, top: 0, bottom: 0 }, at: null, query: '', kind: choice.kind, kinds: false, span: null, favorites: [] };
    if (choice.kind === 'name') return link(state, m.entity, m.entity.refKey, false);
    const existing = spanOver(ed.state.doc, range.from, range.to);
    const same = existing && existing.from === range.from && existing.to === range.to;
    assignSpan(state, same ? [...new Set([...existing.keys, m.entity.refKey])] : [m.entity.refKey], choice.role);
  };

  // Text von außen geändert (z. B. umbenannter Verweisschlüssel): übernehmen, solange man nicht gerade hier schreibt bzw. verknüpft
  // (der Referenz-Browser merkt sich die Stelle im Text; erst nach dem Schließen wird übernommen)
  const linking = browser !== null || creating !== null || wording !== null || pinMenu !== null || timeEditor !== null;
  useEffect(() => {
    if (!editor || value === lastEmitted.current || editor.isFocused || linking) return;
    lastEmitted.current = value;
    if (isField) editor.commands.setContent(fieldToDoc(value), { emitUpdate: false });
    else editor.commands.setContent(value, { contentType: 'markdown', emitUpdate: false });
  }, [editor, value, isField, linking]);
  // neue Einträge: Hervorhebung auffrischen
  useEffect(() => {
    if (editor) editor.view.dispatch(editor.state.tr.setMeta(highlightKey, true));
  }, [editor, known.current]); // eslint-disable-line react-hooks/exhaustive-deps
  // andere Vorkommen, ignorierte Stellen, Prüfen an/aus: Textmarker neu berechnen
  useEffect(() => {
    if (editor && withTerms) editor.view.dispatch(editor.state.tr.setMeta(termKey, true).setMeta(timeSuggestKey, true).setMeta(placeShiftKey, true).setMeta(stateHintKey, true).setMeta(timeConflictKey, true).setMeta(settingConflictKey, true).setMeta(povSuggestKey, true).setMeta(sceneSplitKey, true).setMeta(lifeConflictKey, true).setMeta(factConflictKey, true).setMeta(knowledgeLeakKey, true));
  }, [editor, withTerms, terms, ignored, review, refs, setting, !!timeScene, timeScene?.changes, timeScene?.time, ctx]); // eslint-disable-line react-hooks/exhaustive-deps
  // Prüfen beginnt: zum ersten Vorschlag (gleich welcher Art)
  useEffect(() => {
    if (!editor || !review) return;
    const first = openSuggestions(editor.state)[0];
    if (first) editor.chain().setTextSelection(first.from).scrollIntoView().run();
  }, [editor, review]);

  const state = useEditorState({
    editor,
    selector: ({ editor: ed }) => ({
      line: ed.isActive('heading', { level: 1 }) ? '1' : ed.isActive('heading', { level: 2 }) ? '2' : ed.isActive('heading', { level: 3 }) ? '3' : 'p',
      bold: ed.isActive('bold'),
      italic: ed.isActive('italic'),
      strike: ed.isActive('strike'),
      bullet: ed.isActive('bulletList'),
      ordered: ed.isActive('orderedList'),
      quote: ed.isActive('blockquote'),
      linked: ed.isActive('refLink'),
      timed: ed.isActive('timeMark'),
      undo: !isField && ed.can().undo(),
      redo: !isField && ed.can().redo(),
      ...suggestionCounts(ed.state),
      termSure: termKey.getState(ed.state)?.open.filter((h) => h.entityIds.length === 1).length ?? 0,
    }),
  });
  // offene Vorschläge aller Arten (mögliche Verweise, Zeitangaben, Ortswechsel, Zustandsänderungen, Perspektive); Widersprüche zählen eigens
  const kinds = (['ref', 'time', 'place', 'state', 'pov', 'split'] as const).filter((k) => (state?.[k] ?? 0) > 0);
  const suggestTotal = kinds.reduce((n, k) => n + (state?.[k] ?? 0), 0);
  const suggestParts = kinds.map((k: Exclude<SuggestKind, 'conflict'>) => de.suggest.kinds[k](state![k])).join(' · ');
  // beim Prüfen alles erledigt: Prüfen endet
  useEffect(() => {
    if (!review || suggestTotal > 0) return;
    onReview?.(false);
    toast.ok(de.suggest.done);
  }, [review, suggestTotal]); // eslint-disable-line react-hooks/exhaustive-deps

  const btn = (icon: EditorIconKind, title: string, on: boolean, run: () => void, testId: string, opts: { disabled?: boolean; label?: ReactNode; className?: string } = {}) => (
    <button
      type="button"
      className={`rt-tool${on ? ' active' : ''}${opts.label ? ' with-label' : ''}${opts.className ? ` ${opts.className}` : ''}`}
      aria-label={title}
      aria-pressed={on}
      title={title}
      disabled={opts.disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={run}
      data-testid={testId}
    >
      <EditorIcon kind={icon} />
      {opts.label}
    </button>
  );

  if (!editor || !state) return null;
  // Referenz-Browser (über „Verknüpfen“ bzw. Strg+K oder ein getipptes „@“) und das Anlegen eines Eintrags daraus
  const refUi = (
    <>
      {browser && (
        <RefBrowser
          text={browser.text}
          query={browser.query}
          node={node}
          linkedKey={browser.linkedKey}
          others={browser.others.length}
          anchor={browser.anchor}
          onPick={(refKey, all) => {
            const b = browser;
            const entity = refs.get(refKey);
            setBrowser(null);
            if (entity) link(b, entity, refKey, all);
            else closeBrowser(false, b);
          }}
          kind={browser.kind}
          kinds={browser.kinds && !isField}
          role={browser.role}
          span={browser.span}
          favorites={browser.favorites}
          onSpan={(keys, role) => {
            const b = browser;
            setBrowser(null);
            assignSpan(b, keys, role);
          }}
          onUnspan={() => {
            const b = browser;
            setBrowser(null);
            assignSpan(b, [], null);
          }}
          onCreate={(typeId, all, name, span) => {
            setCreating({ typeId, name, state: browser, all, span });
            setBrowser(null);
          }}
          onUnlink={() => {
            const b = browser;
            if (!b.range) return;
            live.current.browsing = false;
            setBrowser(null);
            const chain = editor.chain().focus().setTextSelection(b.range).unsetMark('refLink');
            (b.at === null ? chain.setTextSelection(b.range.to) : chain.deleteRange({ from: b.at, to: tokenEnd(editor.state.doc, b.at) }).setTextSelection(b.at)).setMeta(targetKey, null).run();
            refocus(editor);
          }}
          onClose={(back) => closeBrowser(back)}
        />
      )}
      {wording && (
        <TermPicker
          name={entityName(wording.entity.id) || wording.entity.refKey}
          terms={wordsOf(wording.entity)}
          anchor={wording.state.anchor}
          onPick={(word) => insertWord(wording.state, wording.entity, word)}
          onClose={(back) => {
            const w = wording;
            setWording(null);
            wordingOpen.current = false;
            closeBrowser(back, w.state);
          }}
        />
      )}
      {timeEditor && (
        <TimeMarkEditor
          text={timeEditor.text}
          initial={timeEditor.initial}
          existing={timeEditor.existing}
          preview={
            timeScene
              ? (v) => {
                  // die Zeitmarken davor im Text (ohne die an dieser Stelle) – wie der Server die Zeit der Szene rechnet
                  const before = timeRanges(editor.state.doc)
                    .filter((r) => r.to <= timeEditor.range.from)
                    .map((r) => r.value);
                  return previewMark(ctx, timeScene, before, v);
                }
              : null
          }
          sceneTitle={(id) => {
            const n = ctx.nodes.get(id);
            return n ? displayOf(ctx, n).title : '';
          }}
          anchor={timeEditor.anchor}
          onApply={(v) => {
            const t = timeEditor;
            setTimeEditor(null);
            setTimeMark(t.range, v);
          }}
          onRemove={() => {
            const t = timeEditor;
            setTimeEditor(null);
            editor.chain().focus().setTextSelection(t.range).unsetMark('timeMark').setTextSelection(t.range.to).setMeta(targetKey, null).run();
            toast.ok(de.timeMarks.removed);
          }}
          onClose={closeTimeEditor}
        />
      )}
      {creating && (
        <EntityModal
          dialog={{ typeId: creating.typeId, name: creating.name }}
          more={false}
          onClose={() => {
            const c = creating;
            setCreating(null);
            // nach dem Anlegen geht es bei einem allein stehenden „@“ mit der Wortwahl weiter
            if (!wordingOpen.current) closeBrowser(false, c.state);
          }}
          onCreated={(entity: Entity) =>
            creating.span ? assignSpan(creating.state, [...creating.span.keys, entity.refKey], creating.span.role) : link(creating.state, entity, entity.refKey, creating.all)
          }
        />
      )}
    </>
  );
  if (isField) {
    return (
      <div className="rt-host rt-field-host">
        <EditorContent editor={editor} />
        <RefRemover editor={editor} onRemove={removeRef} />
        {refUi}
      </div>
    );
  }
  return (
    <div className={`rt-host rt-document ${quietClasses(markView, review)}`} data-marks={markView}>
      <div className="rt-toolbar" role="toolbar" aria-label={de.editor.toolbar}>
        <div className="rt-group">
          {btn('undo', de.editor.undo, false, () => editor.chain().focus().undo().run(), 'rt-undo', { disabled: !state.undo })}
          {btn('redo', de.editor.redo, false, () => editor.chain().focus().redo().run(), 'rt-redo', { disabled: !state.redo })}
        </div>
        <span className="rt-sep" aria-hidden="true" />
        <select
          className="rt-style"
          aria-label={de.editor.lineType}
          title={de.editor.lineType}
          value={state.line}
          onChange={(e) => {
            const v = e.target.value;
            const chain = editor.chain().focus();
            if (v === 'p') chain.setParagraph().run();
            else chain.setHeading({ level: Number(v) as 1 | 2 | 3 }).run();
          }}
          data-testid="rt-line-type"
        >
          {LINE_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label()}
            </option>
          ))}
        </select>
        <span className="rt-sep" aria-hidden="true" />
        <div className="rt-group">
          {btn('bold', de.editor.boldShort, state.bold, () => editor.chain().focus().toggleBold().run(), 'rt-bold')}
          {btn('italic', de.editor.italicShort, state.italic, () => editor.chain().focus().toggleItalic().run(), 'rt-italic')}
          {btn('strike', de.editor.strike, state.strike, () => editor.chain().focus().toggleStrike().run(), 'rt-strike')}
        </div>
        <span className="rt-sep" aria-hidden="true" />
        <div className="rt-group">
          {btn('bullets', de.editor.bullets, state.bullet, () => editor.chain().focus().toggleBulletList().run(), 'rt-bullets')}
          {btn('numbered', de.editor.numbered, state.ordered, () => editor.chain().focus().toggleOrderedList().run(), 'rt-numbered')}
          {btn('quote', de.editor.quote, state.quote, () => editor.chain().focus().toggleBlockquote().run(), 'rt-quote')}
        </div>
        <span className="rt-sep" aria-hidden="true" />
        {btn('link', de.editor.linkRefHint(shortcutList()), state.linked, () => openBrowser(), 'rt-link-ref', { label: <span>{de.editor.linkRef}</span>, className: 'rt-link-btn' })}
        {btn('clock', de.timeMarks.buttonHint, state.timed, () => openTimeEditor(), 'rt-time-btn', { label: <span>{de.timeMarks.button}</span>, className: 'rt-link-btn' })}
        {withTerms && suggestTotal > 0 && (
          <button
            type="button"
            className={`rt-tool with-label rt-terms-btn${review ? ' active' : ''}`}
            aria-pressed={review}
            title={`${suggestParts} – ${de.suggest.hint}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onReview?.(!review)}
            data-testid="rt-terms"
          >
            <span className="rt-suggest-swatch" aria-hidden="true" />
            <span>{de.suggest.count(suggestTotal)}</span>
          </button>
        )}
        {withTerms && state.conflict > 0 && (
          <button
            type="button"
            className="rt-tool with-label rt-conflicts-btn"
            title={de.timeConflicts.countHint}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => jumpToSuggestion(editor.view, 1, 'conflict')}
            data-testid="rt-conflicts"
          >
            <span className="rt-conflict-swatch" aria-hidden="true" />
            <span>{de.timeConflicts.count(state.conflict)}</span>
          </button>
        )}
        <span className="muted small grow tool-hint">{de.editor.richHint}</span>
        <div className="seg marks-view" role="group" aria-label={de.editor.markView}>
          {MARK_VIEWS.map((v) => (
            <button
              key={v}
              type="button"
              className={`seg-btn${markView === v ? ' active' : ''}`}
              aria-pressed={markView === v}
              aria-label={de.editor.markViews[v]}
              title={de.editor.markViews[v]}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setMarkView(v)}
              data-testid={`marks-${v}`}
            >
              <span className={`marks-sample ${v}`} aria-hidden="true">
                Aa
              </span>
            </button>
          ))}
        </div>
      </div>
      {withTerms && review && suggestTotal > 0 && (
        <div className="rt-review-bar" role="status" data-testid="rt-review">
          <span className="grow">{de.suggest.reviewIntro(suggestParts)}</span>
          <button type="button" className="btn small" title={de.suggest.nextHint} onMouseDown={(e) => e.preventDefault()} onClick={() => jumpToSuggestion(editor.view, 1)} data-testid="rt-suggest-next">
            {de.suggest.next}
          </button>
          {state.termSure > 0 && (
            <button type="button" className="btn small primary" onMouseDown={(e) => e.preventDefault()} onClick={acceptAll} data-testid="rt-terms-accept-all">
              {de.terms.acceptAll(state.termSure)}
            </button>
          )}
          <button type="button" className="btn small" onClick={() => onReview?.(false)} data-testid="rt-review-done">
            {de.terms.reviewDone}
          </button>
        </div>
      )}
      <div className="rt-body">
        <div className="rt-scroll rt-desk">
          <div className="rt-page-host">
            <EditorContent editor={editor} />
            <RefRemover editor={editor} onRemove={removeRef} />
            <SpanGutter editor={editor} name={keyName} onOpen={openSpan} onRemove={(s) => removeRef({ kind: 'span', range: s })} />
          </div>
          <RefGlance editor={editor} sceneId={node && isScene(node) ? node.id : null} disabled={linking} />
        </div>
        <RefPins
          entities={pinned}
          menu={pinMenu && { entityId: pinMenu.entity.id, text: pinMenu.text, suggest: pinMenu.suggest, anchor: pinMenu.anchor }}
          onOpen={openPin}
          onPick={pickPin}
          onClose={closePin}
          onUnpin={(id) => {
            closePin();
            togglePin(projectId, id);
          }}
        />
      </div>
      {refUi}
    </div>
  );
}
